<template>
  <div
    class="product-images"
    @mouseover.stop="props.images.edges.length > 1 ? showCursor('Next') : null"
    @mouseleave="hideCursor()"
    @click="changeImageIndex(currentImageIndex + 1)"
  >
    <div
      v-if="props.images.edges.length > 1"
      class="image-picker"
      @mouseenter.stop="hideCursor()"
    >
      <div
        v-for="(image, index) in props.images.edges"
        class="image-picker-item"
        @click.stop="changeImageIndex(index)"
      >
        <div
          :class="{
            active: currentImageIndex === index,
          }"
          class="image"
        >
          <CustomImage
            ref="imgItem"
            :sizes="[248, 248]"
            :image="image.node"
            :shopify="true"
          />
        </div>
      </div>
    </div>

    <div ref="mainImage" :class="'--' + props.type" class="main-image">
      <div class="image">
        <Transition
          mode="out-in"
          @enter="onNewMainEnter"
          @leave="onNewMainLeave"
        >
          <CustomImage
            :key="props.images.edges[currentImageIndex].node"
            :image="props.images.edges[currentImageIndex].node"
            :sizes="[393, 720]"
            :shopify="true"
          />
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import { gsap } from "gsap";
import { useCustomCursor } from "@/composables/useCustomCursor";

const { showCursor, hideCursor } = useCustomCursor();
const props = defineProps({
  images: {
    type: Object,
    required: true,
  },
  type: String,
  required: false,
});

const imgItem = shallowRef([]);
const mainImage = shallowRef(null);

const currentImageIndex = ref(0);

const changeImageIndex = (index) => {
  currentImageIndex.value = index % props.images.edges.length;
};

const onNewMainEnter = async (el, done) => {
  const target =
    imgItem.value[currentImageIndex.value].img.$el.querySelector("img");

  if (currentImageIndex.value === 0) {
    mainImage.value.classList.add("--sample");
    mainImage.value.classList.remove("--vertical");
    mainImage.value.classList.remove("--horizontal");
    return;
  }

  if (target.naturalWidth > target.naturalHeight) {
    mainImage.value.classList.remove("--sample");
    mainImage.value.classList.remove("--vertical");
    mainImage.value.classList.add("--horizontal");
  } else if (target.naturalWidth < target.naturalHeight) {
    mainImage.value.classList.remove("--sample");
    mainImage.value.classList.remove("--horizontal");
    mainImage.value.classList.add("--vertical");
  } else {
    mainImage.value.classList.remove("--vertical");
    mainImage.value.classList.remove("--horizontal");
    mainImage.value.classList.add("--sample");
  }

  gsap.fromTo(
    el,
    {
      opacity: 0,
    },
    {
      opacity: 1,
      duration: 0.8,
      ease: "expo.out",
      onComplete: done,
    }
  );
};
const onNewMainLeave = (el, done) => {
  gsap.to(el, {
    opacity: 0,
    duration: 0.2,
    onComplete: done,
  });
};

onBeforeUnmount(() => {
  hideCursor();
});

onUnmounted(() => {
  hideCursor();
});
</script>

<style lang="scss" scoped>
.product-images {
  background-color: $beige;
  padding-bottom: 4rem;

  @include desktop {
    flex: 1;
    max-width: 50%;
    height: 100vh;
    padding-bottom: 0;
    position: sticky;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.image-picker {
  @include grid-mobile;
  row-gap: 1.2rem;

  margin-bottom: 1.3rem;

  z-index: 2;

  @include desktop {
    display: flex;
    flex-direction: column;
    position: absolute;
    bottom: 2.4rem;
    left: 2.4rem;
    gap: 1rem;
  }
}

.image-picker-item {
  grid-column: span 3;
  .image {
    aspect-ratio: 83 / 64;
    width: 100%;
    height: 100%;
    :deep(img) {
      object-fit: cover;
    }
  }

  @include desktop {
    grid-column: unset;
    cursor: pointer;
    .image {
      aspect-ratio: 36 / 48;
      height: 4.8rem;
    }
  }
}

.main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;

  display: flex;
  justify-content: center;
  align-items: center;

  @include desktop {
    aspect-ratio: unset;

    &.--horizontal {
      padding: 0 var(--padding-mobile);
      width: 100%;
      height: auto;
      @include desktop {
        padding: 0 $padding-desktop;
      }
    }

    &.--vertical {
      width: 100%;
      height: 100%;
      object-fit: cover;
      padding: 0;
    }

    &.--sample,
    &.sample {
      aspect-ratio: 1 / 1;
      max-width: 65%;
      height: auto;
    }

    &.--product {
      max-width: 65%;
      height: auto;
    }
  }
}

.image {
  aspect-ratio: 369 / 303;
  width: 100%;
  height: 100%;
  :deep(img) {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
  @include desktop {
    aspect-ratio: unset;
  }
  &.active {
    border: solid 1px black;
  }
}
</style>
